import logger from "../utils/logger";
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { DollarSign, Plus, FileText, CheckCircle, Eye, Printer, Users, Clock, CheckCircle2 } from 'lucide-react';

import API_URL from '../utils/api';

const Payroll = () => {
    const [payrolls, setPayrolls] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [viewingPayslip, setViewingPayslip] = useState(null);
    const [formData, setFormData] = useState({
        employee_id: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        basic_salary: '',
        overtime_hours: '0',
        overtime_rate: '0',
        bonus: '0',
        deductions: '0',
        loan_deduction: '0',
        advance_deduction: '0',
        tax: '0',
        allowances: '0'
    });

    const fetchData = useCallback(async () => {
        try {
            const [payrollRes, empRes] = await Promise.all([
                axios.get(`${API_URL}/api/payroll`, { withCredentials: true }),
                axios.get(`${API_URL}/api/employees`, { withCredentials: true })
            ]);
            setPayrolls(payrollRes.data);
            setEmployees(empRes.data.filter(e => e.role !== 'admin'));
        } catch (error) {
            logger.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleEmployeeChange = (employeeId) => {
        const employee = employees.find(e => e._id === employeeId);
        setFormData({
            ...formData,
            employee_id: employeeId,
            basic_salary: employee?.basic_salary?.toString() || ''
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                basic_salary: parseFloat(formData.basic_salary) || 0,
                overtime_hours: parseFloat(formData.overtime_hours) || 0,
                overtime_rate: parseFloat(formData.overtime_rate) || 0,
                bonus: parseFloat(formData.bonus) || 0,
                deductions: parseFloat(formData.deductions) || 0,
                loan_deduction: parseFloat(formData.loan_deduction) || 0,
                advance_deduction: parseFloat(formData.advance_deduction) || 0,
                tax: parseFloat(formData.tax) || 0,
                allowances: parseFloat(formData.allowances) || 0
            };

            await axios.post(`${API_URL}/api/payroll`, payload, { withCredentials: true });
            setIsDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error creating payroll');
        }
    };

    const handleProcess = async (payrollId) => {
        try {
            await axios.put(`${API_URL}/api/payroll/${payrollId}/process`, {}, { withCredentials: true });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error processing payroll');
        }
    };

    const handleDownloadPayslip = async (payrollId) => {
        try {
            const response = await axios.get(`${API_URL}/api/payroll/${payrollId}/payslip-pdf`, {
                withCredentials: true,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `payslip_${payrollId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            alert('Error downloading payslip');
        }
    };

    const handleBulkDownload = async () => {
        const month = new Date().getMonth() + 1;
        const year = new Date().getFullYear();
        try {
            const response = await axios.post(`${API_URL}/api/payroll/bulk-payslips?month=${month}&year=${year}`, {}, {
                withCredentials: true,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `payslips_bulk_${month}_${year}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            alert('Error downloading bulk payslips');
        }
    };

    const resetForm = () => {
        setFormData({
            employee_id: '',
            month: new Date().getMonth() + 1,
            year: new Date().getFullYear(),
            basic_salary: '',
            overtime_hours: '0',
            overtime_rate: '0',
            bonus: '0',
            deductions: '0',
            loan_deduction: '0',
            advance_deduction: '0',
            tax: '0',
            allowances: '0'
        });
    };

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const printPayslip = () => {
        window.print();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-slate-500">Loading payroll data...</div>
            </div>
        );
    }

    // Premium summary metrics
    const totalGross = payrolls.reduce((s, p) => s + (p.gross_salary || 0), 0);
    const totalNet = payrolls.reduce((s, p) => s + (p.net_salary || 0), 0);
    const pendingCount = payrolls.filter(p => p.status === 'pending').length;
    const processedCount = payrolls.filter(p => p.status === 'processed').length;
    const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

    return (
        <div className="space-y-6" data-testid="payroll-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Payroll Management
                    </h1>
                    <p className="text-slate-500 mt-1">Process and manage employee salaries</p>
                </div>
                
                <div className="flex gap-3">
                    <Button variant="outline" className="rounded-lg shadow-sm" onClick={handleBulkDownload} data-testid="bulk-download-btn">
                        <Printer className="h-4 w-4 mr-2" />
                        Bulk PDF
                    </Button>
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="create-payroll-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                Create Payroll
                            </Button>
                        </DialogTrigger>
                    <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                Create Payroll Entry
                            </DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Calculate and create a payroll record with salary, allowances and deductions.</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Employee</Label>
                                    <Select value={formData.employee_id} onValueChange={handleEmployeeChange}>
                                        <SelectTrigger className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="payroll-employee-select">
                                            <SelectValue placeholder="Select employee" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-lg">
                                            {employees.map(emp => (
                                                <SelectItem key={emp._id} value={emp._id}>
                                                    {emp.name} ({emp.employee_id})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Month</Label>
                                    <Select value={formData.month.toString()} onValueChange={(v) => setFormData({...formData, month: parseInt(v)})}>
                                        <SelectTrigger className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="payroll-month-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-lg">
                                            {months.map((m, i) => (
                                                <SelectItem key={m} value={(i + 1).toString()}>{m}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Year</Label>
                                    <Input
                                        type="number"
                                        value={formData.year}
                                        onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        required
                                        data-testid="payroll-year-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Basic Salary</Label>
                                    <Input
                                        type="number"
                                        value={formData.basic_salary}
                                        onChange={(e) => setFormData({...formData, basic_salary: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        required
                                        data-testid="payroll-basic-salary-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Allowances</Label>
                                    <Input
                                        type="number"
                                        value={formData.allowances}
                                        onChange={(e) => setFormData({...formData, allowances: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        data-testid="payroll-allowances-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Overtime Hours</Label>
                                    <Input
                                        type="number"
                                        value={formData.overtime_hours}
                                        onChange={(e) => setFormData({...formData, overtime_hours: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        data-testid="payroll-overtime-hours-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Overtime Rate ($/hr)</Label>
                                    <Input
                                        type="number"
                                        value={formData.overtime_rate}
                                        onChange={(e) => setFormData({...formData, overtime_rate: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        data-testid="payroll-overtime-rate-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Bonus</Label>
                                    <Input
                                        type="number"
                                        value={formData.bonus}
                                        onChange={(e) => setFormData({...formData, bonus: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        data-testid="payroll-bonus-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Deductions</Label>
                                    <Input
                                        type="number"
                                        value={formData.deductions}
                                        onChange={(e) => setFormData({...formData, deductions: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        data-testid="payroll-deductions-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Loan Deduction</Label>
                                    <Input
                                        type="number"
                                        value={formData.loan_deduction}
                                        onChange={(e) => setFormData({...formData, loan_deduction: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        data-testid="payroll-loan-deduction-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Advance Deduction</Label>
                                    <Input
                                        type="number"
                                        value={formData.advance_deduction}
                                        onChange={(e) => setFormData({...formData, advance_deduction: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        data-testid="payroll-advance-deduction-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Tax</Label>
                                    <Input
                                        type="number"
                                        value={formData.tax}
                                        onChange={(e) => setFormData({...formData, tax: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        data-testid="payroll-tax-input"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" className="rounded-lg" onClick={() => setIsDialogOpen(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="payroll-save-btn">
                                    Create Payroll
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            {/* Premium Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="payroll-summary-grid">
                <Card className="group relative rounded-xl border border-slate-200 bg-gradient-to-br from-emerald-50/60 to-white overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300">
                    <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-emerald-500 to-teal-500" />
                    <CardContent className="p-5 flex items-start justify-between">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Total Net Payout</p>
                            <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{fmt(totalNet)}</p>
                            <p className="text-xs text-slate-500 mt-1">{payrolls.length} entries</p>
                        </div>
                        <div className="flex-shrink-0 p-2.5 rounded-lg bg-emerald-100"><DollarSign className="h-5 w-5 text-emerald-600" /></div>
                    </CardContent>
                </Card>
                <Card className="group relative rounded-xl border border-slate-200 bg-gradient-to-br from-blue-50/60 to-white overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300">
                    <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-[#2563eb] to-[#3b82f6]" />
                    <CardContent className="p-5 flex items-start justify-between">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Total Gross</p>
                            <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{fmt(totalGross)}</p>
                            <p className="text-xs text-slate-500 mt-1">Pre-deductions</p>
                        </div>
                        <div className="flex-shrink-0 p-2.5 rounded-lg bg-blue-100"><Users className="h-5 w-5 text-[#2563eb]" /></div>
                    </CardContent>
                </Card>
                <Card className="group relative rounded-xl border border-slate-200 bg-gradient-to-br from-amber-50/60 to-white overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300">
                    <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-amber-500 to-orange-500" />
                    <CardContent className="p-5 flex items-start justify-between">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Pending</p>
                            <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{pendingCount}</p>
                            <p className="text-xs text-slate-500 mt-1">Awaiting process</p>
                        </div>
                        <div className="flex-shrink-0 p-2.5 rounded-lg bg-amber-100"><Clock className="h-5 w-5 text-amber-600" /></div>
                    </CardContent>
                </Card>
                <Card className="group relative rounded-xl border border-slate-200 bg-gradient-to-br from-violet-50/60 to-white overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300">
                    <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
                    <CardContent className="p-5 flex items-start justify-between">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Processed</p>
                            <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{processedCount}</p>
                            <p className="text-xs text-slate-500 mt-1">Disbursed</p>
                        </div>
                        <div className="flex-shrink-0 p-2.5 rounded-lg bg-violet-100"><CheckCircle2 className="h-5 w-5 text-violet-600" /></div>
                    </CardContent>
                </Card>
            </div>

            {/* Payroll List */}
            <Card className="rounded-xl shadow-sm border-slate-200 overflow-hidden">
                <CardHeader className="border-b border-slate-200 bg-slate-50/50">
                    <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <DollarSign className="h-5 w-5 text-[#2563eb]" />
                        Payroll Records
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="payroll-table">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50">
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Employee</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Period</th>
                                    <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Gross</th>
                                    <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Deductions</th>
                                    <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Net Salary</th>
                                    <th className="text-center p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                                    <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payrolls.map((payroll) => (
                                    <tr key={payroll._id} className="border-b border-slate-200 hover:bg-slate-50">
                                        <td className="p-4">
                                            <div>
                                                <p className="font-medium text-slate-900">{payroll.employee_name}</p>
                                                <p className="text-sm text-slate-500">{payroll.employee_code}</p>
                                            </div>
                                        </td>
                                        <td className="p-4 text-slate-700">
                                            {months[payroll.month - 1]} {payroll.year}
                                        </td>
                                        <td className="p-4 text-right font-medium text-slate-900">
                                            ${payroll.gross_salary?.toLocaleString()}
                                        </td>
                                        <td className="p-4 text-right text-red-600">
                                            -${payroll.total_deductions?.toLocaleString()}
                                        </td>
                                        <td className="p-4 text-right font-bold text-green-600">
                                            ${payroll.net_salary?.toLocaleString()}
                                        </td>
                                        <td className="p-4 text-center">
                                            <Badge className={`rounded-full px-3 py-0.5 ${payroll.status === 'processed' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`}>
                                                {payroll.status}
                                            </Badge>
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="rounded-lg"
                                                    onClick={() => setViewingPayslip(payroll)}
                                                    data-testid={`view-payslip-${payroll._id}`}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                                {payroll.status === 'pending' && (
                                                    <Button
                                                        size="sm"
                                                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-sm"
                                                        onClick={() => handleProcess(payroll._id)}
                                                        data-testid={`process-payroll-${payroll._id}`}
                                                    >
                                                        <CheckCircle className="h-4 w-4 mr-1" />
                                                        Process
                                                    </Button>
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="rounded-lg"
                                                    onClick={() => handleDownloadPayslip(payroll._id)}
                                                    data-testid={`download-payslip-${payroll._id}`}
                                                >
                                                    <Printer className="h-4 w-4 mr-1" />
                                                    PDF
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {payrolls.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="p-8 text-center text-slate-500">
                                            No payroll records found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            {/* Payslip Modal */}
            <Dialog open={!!viewingPayslip} onOpenChange={() => setViewingPayslip(null)}>
                <DialogContent className="rounded-2xl max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <FileText className="h-5 w-5 text-[#2563eb]" />
                            Payslip
                        </DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Detailed payslip view with earnings, deductions and net salary.</DialogDescription>
                    </DialogHeader>
                    {viewingPayslip && (
                        <div className="mt-4 space-y-6" data-testid="payslip-modal">
                            {/* Company Header */}
                            <div className="text-center border-b border-slate-200 pb-4">
                                <h2 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    DIGI-LOANS
                                </h2>
                                <p className="text-slate-500">HR & Payroll Department</p>
                                <p className="text-lg font-bold mt-2">
                                    Payslip for {months[viewingPayslip.month - 1]} {viewingPayslip.year}
                                </p>
                            </div>

                            {/* Employee Details */}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-slate-500">Employee Name</p>
                                    <p className="font-medium text-slate-900">{viewingPayslip.employee_name}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">Employee ID</p>
                                    <p className="font-medium text-slate-900">{viewingPayslip.employee_code}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">Department</p>
                                    <p className="font-medium text-slate-900">{viewingPayslip.department}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">Pay Period</p>
                                    <p className="font-medium text-slate-900">{months[viewingPayslip.month - 1]} {viewingPayslip.year}</p>
                                </div>
                            </div>

                            {/* Earnings & Deductions */}
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <h3 className="font-bold text-slate-900 border-b border-slate-200 pb-2 mb-3">Earnings</h3>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Basic Salary</span>
                                            <span className="font-medium">${viewingPayslip.basic_salary?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Allowances</span>
                                            <span className="font-medium">${viewingPayslip.allowances?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Overtime ({viewingPayslip.overtime_hours}hrs)</span>
                                            <span className="font-medium">${viewingPayslip.overtime_pay?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Bonus</span>
                                            <span className="font-medium">${viewingPayslip.bonus?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between border-t border-slate-200 pt-2 font-bold">
                                            <span>Gross Salary</span>
                                            <span className="text-green-600">${viewingPayslip.gross_salary?.toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900 border-b border-slate-200 pb-2 mb-3">Deductions</h3>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Tax</span>
                                            <span className="font-medium text-red-600">-${viewingPayslip.tax?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Deductions</span>
                                            <span className="font-medium text-red-600">-${viewingPayslip.deductions?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Loan Deduction</span>
                                            <span className="font-medium text-red-600">-${viewingPayslip.loan_deduction?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Advance Deduction</span>
                                            <span className="font-medium text-red-600">-${viewingPayslip.advance_deduction?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between border-t border-slate-200 pt-2 font-bold">
                                            <span>Total Deductions</span>
                                            <span className="text-red-600">-${viewingPayslip.total_deductions?.toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Net Salary */}
                            <div className="bg-gradient-to-r from-[#2563eb] to-[#1d4ed8] p-4 -mx-6 -mb-6 flex justify-between items-center rounded-b-2xl">
                                <span className="text-white font-bold text-lg">Net Salary</span>
                                <span className="text-white font-black text-2xl" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    ${viewingPayslip.net_salary?.toLocaleString()}
                                </span>
                            </div>

                            <div className="flex justify-end pt-4 no-print">
                                <Button onClick={printPayslip} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="print-payslip-btn">
                                    <Printer className="h-4 w-4 mr-2" />
                                    Print Payslip
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Payroll;
